import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, X, Trash2, Phone, Mail, MapPin, MessageCircle, StickyNote, Mic, Square,
  Sparkles, ShoppingCart, CalendarClock, Users, Target, Loader2, Check, FileSignature, Send, FileText,
  Paperclip, ImageIcon, ChevronDown, ChevronUp, ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import api from "./api";

const STAGES = [
  { id: "yeni_talep",     tr: "Yeni Talep",       en: "New Request",     color: "#7FB3D8" },
  { id: "iletisim",       tr: "İletişim Kuruldu", en: "Contacted",       color: "#8E97E6" },
  { id: "teklif_verildi", tr: "Teklif Verildi",   en: "Quote Sent",      color: "#8FB8C6" },
  { id: "pazarlik",       tr: "Pazarlık",         en: "Negotiation",     color: "#D9B172" },
  { id: "kazanildi",      tr: "Kazanıldı",        en: "Won",             color: "#4FBF8B" },
  { id: "kaybedildi",     tr: "Kaybedildi",       en: "Lost",            color: "#D77A7A" },
];

const SOURCES = [
  { id: "telefon", tr: "Telefon", en: "Phone" },
  { id: "whatsapp", tr: "WhatsApp", en: "WhatsApp" },
  { id: "web", tr: "Web", en: "Web" },
  { id: "fuar", tr: "Fuar", en: "Trade Fair" },
  { id: "referans", tr: "Referans", en: "Referral" },
  { id: "diger", tr: "Diğer", en: "Other" },
];

const NOTE_TYPES = [
  { id: "note", tr: "Not", en: "Note", icon: StickyNote },
  { id: "call", tr: "Telefon", en: "Call", icon: Phone },
  { id: "whatsapp", tr: "WhatsApp", en: "WhatsApp", icon: MessageCircle },
  { id: "email", tr: "E-posta", en: "Email", icon: Mail },
  { id: "visit", tr: "Ziyaret", en: "Visit", icon: MapPin },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const SOURCES_TR = [
  { id: "telefon", label: "Telefon" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "web", label: "Web" },
  { id: "fuar", label: "Fuar" },
  { id: "referans", label: "Referans" },
  { id: "diger", label: "Diğer" },
];

/** CRM Ana Sayfa — Sesli Kayıt → Düzenlenebilir Form → Kaydet */
function VoiceNewLeadButton({ tr, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seconds, setSeconds] = useState(0);
  // extracted: ham AI çıktısı (dry_run=true) — form buradan doldurulur
  const [extracted, setExtracted] = useState(null); // {transcript, summary, extracted:{...}}
  const [form, setForm] = useState(null);            // kullanıcının düzenleyebileceği form alanları
  const [showTranscript, setShowTranscript] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("durationSec", String(seconds));
          fd.append("dry_run", "true"); // kaydetme, sadece çıkar
          const { data } = await api.post("/crm/leads/from-voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setExtracted(data);
          setForm({ ...data.extracted }); // formu AI çıktısıyla doldur
          setShowTranscript(false);
        } catch (e) {
          toast.error("Ses işlenemedi — " + (e?.response?.data?.detail || e.message));
        } finally { setUploading(false); }
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { toast.error("Mikrofona erişilemedi"); }
  };

  const stop = () => { stopTimer(); setRecording(false); try { recRef.current?.stop(); } catch {} };
  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form?.title?.trim()) { toast.error("Talep başlığı boş bırakılamaz"); return; }
    setSaving(true);
    try {
      await api.post("/crm/leads", { ...form, stage: "yeni_talep" });
      toast.success("Talep kaydedildi!");
      setExtracted(null);
      setForm(null);
      onSaved();
    } catch (e) {
      toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  /* ─── Düzenlenebilir Form Modali ─── */
  if (extracted && form) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="voice-lead-edit-modal">
        <div className="zen-panel w-full max-w-lg max-h-[92vh] overflow-y-auto zen-anim-up">
          {/* Başlık */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-[#22304A]">
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2">
                <Mic className="w-4 h-4 text-violet-500" /> Sesli Talep — Onaylamadan Önce Düzenle
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">AI çıkarılan alanları kontrol edip düzenleyebilirsiniz</p>
            </div>
            <button onClick={() => { setExtracted(null); setForm(null); }}><X className="w-4 h-4" /></button>
          </div>

          <div className="p-4 space-y-3">
            {/* Transkript bölümü */}
            <div className="rounded-xl bg-violet-500/5 border border-violet-400/20 p-3">
              <button
                className="w-full flex items-center justify-between text-xs font-semibold text-violet-600 dark:text-violet-400"
                onClick={() => setShowTranscript(t => !t)}
                data-testid="voice-transcript-toggle"
              >
                <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Transkript & AI Özeti</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showTranscript ? "rotate-180" : ""}`} />
              </button>
              {showTranscript && (
                <div className="mt-2 space-y-2">
                  {extracted.transcript && (
                    <div>
                      <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">Ham Transkript (Whisper)</div>
                      <div className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-[#0C1220] rounded p-2 leading-relaxed italic">
                        {extracted.transcript}
                      </div>
                    </div>
                  )}
                  {extracted.summary && (
                    <div>
                      <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">AI Özeti</div>
                      <div className="text-[11px] text-slate-600 dark:text-slate-300">{extracted.summary}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Form alanları */}
            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Talep Başlığı *</label>
              <input
                data-testid="voice-lead-title"
                className="zen-input w-full !text-sm"
                value={form.title || ""}
                onChange={e => setF("title", e.target.value)}
                placeholder="Örn: Villa stor perde talebi"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Müşteri Adı</label>
                <input
                  data-testid="voice-lead-customer"
                  className="zen-input w-full !text-xs"
                  value={form.customerName || ""}
                  onChange={e => setF("customerName", e.target.value)}
                  placeholder="Ad Soyad"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Telefon</label>
                <input
                  data-testid="voice-lead-phone"
                  className="zen-input w-full !text-xs"
                  value={form.phone || ""}
                  onChange={e => setF("phone", e.target.value)}
                  placeholder="05xx xxx xx xx"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">E-posta</label>
                <input
                  data-testid="voice-lead-email"
                  className="zen-input w-full !text-xs"
                  value={form.email || ""}
                  onChange={e => setF("email", e.target.value)}
                  placeholder="email@ornek.com"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Şehir</label>
                <input
                  data-testid="voice-lead-city"
                  className="zen-input w-full !text-xs"
                  value={form.city || ""}
                  onChange={e => setF("city", e.target.value)}
                  placeholder="İstanbul, Ankara…"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Proje Tipi</label>
              <input
                data-testid="voice-lead-project-type"
                className="zen-input w-full !text-xs"
                value={form.projectType || ""}
                onChange={e => setF("projectType", e.target.value)}
                placeholder="villa, daire, ofis, mağaza…"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Kaynak</label>
                <select
                  data-testid="voice-lead-source"
                  className="zen-input w-full !text-xs"
                  value={form.source || "telefon"}
                  onChange={e => setF("source", e.target.value)}
                >
                  {SOURCES_TR.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Takip Tarihi</label>
                <input
                  data-testid="voice-lead-followup"
                  type="date"
                  className="zen-input w-full !text-xs"
                  value={form.nextFollowUp || ""}
                  onChange={e => setF("nextFollowUp", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Tahmini Değer</label>
                <input
                  data-testid="voice-lead-value"
                  type="number"
                  className="zen-input w-full !text-xs"
                  value={form.estimatedValue || 0}
                  min="0"
                  onChange={e => setF("estimatedValue", Math.max(0, parseFloat(e.target.value) || 0))}
                  min="0"
                />
              </div>
              <div>
                <label className="text-[11px] text-slate-500 block mb-1">Para Birimi</label>
                <select
                  data-testid="voice-lead-currency"
                  className="zen-input w-full !text-xs"
                  value={form.currency || "TRY"}
                  onChange={e => setF("currency", e.target.value)}
                >
                  <option value="TRY">TRY</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[11px] text-slate-500 block mb-1">Notlar (AI Özeti)</label>
              <textarea
                data-testid="voice-lead-notes"
                className="zen-input w-full !text-xs resize-none"
                rows={3}
                value={form.notes || ""}
                onChange={e => setF("notes", e.target.value)}
                placeholder="Ek notlar, müşteri istekleri..."
              />
            </div>
          </div>

          {/* Alt butonlar */}
          <div className="p-4 border-t border-slate-200 dark:border-[#22304A] flex gap-2">
            <button
              onClick={() => { setExtracted(null); setForm(null); }}
              className="zen-btn-ghost flex-1 !text-xs"
              data-testid="voice-lead-discard"
            >
              İptal
            </button>
            <button
              onClick={save}
              disabled={saving || !form?.title?.trim()}
              className="zen-btn-primary flex-1 !text-xs disabled:opacity-50"
              data-testid="voice-lead-save"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Kaydediliyor…</> : <><Check className="w-3.5 h-3.5 inline mr-1" />Talebi Kaydet</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Kayıt Butonu ─── */
  return (
    <button
      data-testid="crm-voice-new-lead-btn"
      onClick={recording ? stop : start}
      disabled={uploading}
      className={`zen-btn-ghost !py-2 !px-3.5 !text-xs flex items-center gap-1.5 transition-all ${
        recording ? "!border-rose-500 !text-rose-500 animate-pulse" : ""
      } ${uploading ? "opacity-60 cursor-wait" : ""}`}
      title={recording ? "Kaydı Durdur & AI'a Gönder" : "Sesli Talep Oluştur"}
    >
      {uploading ? (
        <><Loader2 className="w-4 h-4 animate-spin" /> AI İşliyor…</>
      ) : recording ? (
        <><Square className="w-4 h-4" /> {fmt(seconds)} Dur</>
      ) : (
        <><Mic className="w-4 h-4" /> Sesli Talep</>
      )}
    </button>
  );
}

export default function CRMModule({ lang = "tr", tr = (s) => s, onGenerateQuotePDF, onOpenOrderForm }) {
  const L = (o) => (lang === "en" ? o.en : o.tr);
  const [view, setView] = useState("pipeline");
  const [leads, setLeads] = useState([]);
  const [followups, setFollowups] = useState({ overdue: [], today: [], upcoming: [] });
  const [customers, setCustomers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [openLead, setOpenLead] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [l, f] = await Promise.all([api.get("/crm/leads"), api.get("/crm/followups")]);
      setLeads(l.data || []);
      setFollowups(f.data || { overdue: [], today: [], upcoming: [] });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data || [])).catch(() => {});
    api.get("/quotes").then(r => setQuotes(r.data || [])).catch(() => {});
  }, []);

  const moveStage = async (lead, stage) => {
    if (lead.stage === stage) return;
    setLeads(prev => prev.map(l => (l.id === lead.id ? { ...l, stage } : l)));
    try {
      await api.put(`/crm/leads/${lead.id}`, { stage });
      toast.success(tr("Aşama güncellendi"));
      load();
    } catch { load(); }
  };

  const totalValue = (stageId) =>
    leads.filter(l => l.stage === stageId).reduce((s, l) => s + Number(l.estimatedValue || 0), 0);

  return (
    <div className="space-y-5" data-testid="crm-module">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
          <span className="relative w-9 h-9 rounded-[11px] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#94A9DE] via-[#5468A8] to-[#2F3E6B] ring-1 ring-black/10">
            <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/32 via-white/8 to-transparent" />
            <Target className="relative w-[18px] h-[18px] text-white" strokeWidth={2} />
          </span>
          {tr("CRM — Aday Müşteri Takibi")}
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl overflow-hidden border border-slate-200 dark:border-[#22304A]">
            {[{ id: "pipeline", label: tr("Satış Hattı") }, { id: "followups", label: tr("Takip Listesi") }].map(v => (
              <button key={v.id} data-testid={`crm-view-${v.id}`} onClick={() => setView(v.id)}
                className={`px-3 py-2 text-xs font-bold transition-colors ${view === v.id
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
                {v.label}
              </button>
            ))}
          </div>
          <button data-testid="crm-new-lead-btn" onClick={() => setShowNew(true)} className="zen-btn-primary !py-2 !px-3.5 !text-xs">
            <Plus className="w-4 h-4" /> {tr("Yeni Aday")}
          </button>
          <VoiceNewLeadButton tr={tr} onSaved={load} />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">{tr("Yükleniyor...")}</div>
      ) : view === "pipeline" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="crm-pipeline">
          {STAGES.map(st => (
            <div key={st.id} data-testid={`crm-column-${st.id}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { const id = e.dataTransfer.getData("leadId"); const lead = leads.find(l => l.id === id); if (lead) moveStage(lead, st.id); }}
              className="zen-panel p-3 min-h-[220px]">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.color }} />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">{L(st)}</span>
                </div>
                <span className="text-[10px] font-bold text-slate-400">{leads.filter(l => l.stage === st.id).length}</span>
              </div>
              <div className="text-[10px] zen-mono text-slate-400 mb-2">€{totalValue(st.id).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")}</div>
              <div className="space-y-2">
                {leads.filter(l => l.stage === st.id).map(l => {
                  const late = l.nextFollowUp && l.nextFollowUp < todayISO();
                  return (
                    <div key={l.id} data-testid={`crm-lead-card-${l.id}`} draggable
                      onDragStart={(e) => e.dataTransfer.setData("leadId", l.id)}
                      onClick={() => setOpenLead(l.id)}
                      className="rounded-xl p-2.5 cursor-pointer border border-slate-200/70 dark:border-[#22304A] bg-white/70 dark:bg-white/[0.04] hover:-translate-y-0.5 transition-transform">
                      <div className="text-[12px] font-bold text-slate-800 dark:text-slate-100 leading-tight">{l.title || l.customerName}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate">{l.customerName}</div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] zen-mono font-semibold text-slate-600 dark:text-slate-300">€{Number(l.estimatedValue || 0).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")}</span>
                        {l.nextFollowUp && (
                          <span className={`text-[9.5px] px-1.5 py-0.5 rounded-md font-bold ${late ? "bg-rose-500/15 text-rose-500" : "bg-sky-500/15 text-sky-500"}`}>
                            {l.nextFollowUp.slice(5)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid="crm-followups">
          {[
            { key: "overdue", label: tr("Gecikmiş"), tone: "text-rose-500" },
            { key: "today", label: tr("Bugün"), tone: "text-amber-500" },
            { key: "upcoming", label: tr("Yaklaşan"), tone: "text-sky-500" },
          ].map(g => (
            <div key={g.key} className="zen-panel p-4" data-testid={`crm-followup-${g.key}`}>
              <div className={`text-[11px] font-bold uppercase tracking-wider mb-2.5 ${g.tone}`}>
                {g.label} ({(followups[g.key] || []).length})
              </div>
              {(followups[g.key] || []).length === 0 ? (
                <div className="text-[12px] text-slate-400">{tr("Kayıt yok")}</div>
              ) : (
                <table className="zen-table w-full text-[12px]">
                  <thead><tr>
                    <th>{tr("Tarih")}</th><th>{tr("Aday / Talep")}</th><th>{tr("Müşteri")}</th>
                    <th>{tr("Telefon")}</th><th>{tr("Aşama")}</th><th className="text-right">{tr("Tutar")}</th>
                  </tr></thead>
                  <tbody>
                    {(followups[g.key] || []).map(l => (
                      <tr key={l.id} className="cursor-pointer" onClick={() => setOpenLead(l.id)} data-testid={`crm-followup-row-${l.id}`}>
                        <td className="zen-mono">{l.nextFollowUp}</td>
                        <td className="font-semibold">{l.title || "—"}</td>
                        <td>{l.customerName}</td>
                        <td className="zen-mono">{l.phone}</td>
                        <td>{L(STAGES.find(s => s.id === l.stage) || STAGES[0])}</td>
                        <td className="text-right zen-mono">€{Number(l.estimatedValue || 0).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <LeadForm lang={lang} tr={tr} customers={customers} quotes={quotes}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }} />
      )}
      {openLead && (
        <LeadDetail leadId={openLead} lang={lang} tr={tr} customers={customers} quotes={quotes}
          onClose={() => setOpenLead(null)} onChanged={load} onGenerateQuotePDF={onGenerateQuotePDF}
          onOpenOrderForm={onOpenOrderForm} />
      )}
    </div>
  );
}

function LeadForm({ lang, tr, customers, quotes, onClose, onSaved, initial }) {
  const L = (o) => (lang === "en" ? o.en : o.tr);
  const [f, setF] = useState(initial || {
    title: "", customerId: "", customerName: "", contactName: "", phone: "", email: "",
    source: "telefon", stage: "yeni_talep", estimatedValue: 0, currency: "EUR",
    quoteId: "", nextFollowUp: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.customerName && !f.title) { toast.error(tr("Müşteri adı veya talep başlığı gerekli")); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/crm/leads/${initial.id}`, f);
      else await api.post("/crm/leads", f);
      toast.success(tr("Kaydedildi"));
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="zen-panel w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="crm-lead-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-extrabold">{initial?.id ? tr("Adayı Düzenle") : tr("Yeni Aday Müşteri")}</h3>
          <button onClick={onClose} data-testid="crm-lead-form-close" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label={tr("Talep Başlığı")} className="col-span-2">
            <input data-testid="crm-field-title" className="zen-input w-full" value={f.title} onChange={e => set("title", e.target.value)} />
          </Field>
          <Field label={tr("Mevcut Cari")} className="col-span-2">
            <select data-testid="crm-field-customer" className="zen-input w-full" value={f.customerId}
              onChange={e => {
                const c = customers.find(x => x.id === e.target.value);
                setF(p => ({ ...p, customerId: e.target.value, customerName: c ? c.name : p.customerName, phone: c?.phone || p.phone, email: c?.email || p.email }));
              }}>
              <option value="">{tr("— Yeni / cariye bağlı değil —")}</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label={tr("Müşteri Adı")}>
            <input data-testid="crm-field-customer-name" className="zen-input w-full" value={f.customerName} onChange={e => set("customerName", e.target.value)} />
          </Field>
          <Field label={tr("İlgili Kişi")}>
            <input className="zen-input w-full" value={f.contactName} onChange={e => set("contactName", e.target.value)} />
          </Field>
          <Field label={tr("Telefon")}>
            <input data-testid="crm-field-phone" className="zen-input w-full" value={f.phone} onChange={e => set("phone", e.target.value)} />
          </Field>
          <Field label={tr("E-posta")}>
            <input className="zen-input w-full" value={f.email} onChange={e => set("email", e.target.value)} />
          </Field>
          <Field label={tr("Kaynak")}>
            <select className="zen-input w-full" value={f.source} onChange={e => set("source", e.target.value)}>
              {SOURCES.map(s => <option key={s.id} value={s.id}>{L(s)}</option>)}
            </select>
          </Field>
          <Field label={tr("Aşama")}>
            <select data-testid="crm-field-stage" className="zen-input w-full" value={f.stage} onChange={e => set("stage", e.target.value)}>
              {STAGES.map(s => <option key={s.id} value={s.id}>{L(s)}</option>)}
            </select>
          </Field>
          <Field label={tr("Tahmini Tutar")}>
            <input data-testid="crm-field-value" type="number" min="0" className="zen-input w-full" value={f.estimatedValue} onChange={e => set("estimatedValue", Math.max(0, Number(e.target.value) || 0))} />
          </Field>
          <Field label={tr("Para Birimi")}>
            <select className="zen-input w-full" value={f.currency} onChange={e => set("currency", e.target.value)}>
              {["EUR", "TL", "USD"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={tr("Bağlı Teklif")} className="col-span-2">
            <select data-testid="crm-field-quote" className="zen-input w-full" value={f.quoteId} onChange={e => set("quoteId", e.target.value)}>
              <option value="">{tr("— Yok —")}</option>
              {quotes.map(q => <option key={q.id} value={q.id}>{`#${q.quoteNo} · ${q.customerName}`}</option>)}
            </select>
          </Field>
          <Field label={tr("Sonraki Takip")} className="col-span-2">
            <input data-testid="crm-field-followup" type="date" className="zen-input w-full" value={f.nextFollowUp || ""} onChange={e => set("nextFollowUp", e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="zen-btn-ghost !py-2 !px-3.5 !text-xs">{tr("Vazgeç")}</button>
          <button data-testid="crm-lead-save" disabled={saving} onClick={save} className="zen-btn-primary !py-2 !px-3.5 !text-xs">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {tr("Kaydet")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

function LeadDetail({ leadId, lang, tr, customers, quotes, onClose, onChanged, onGenerateQuotePDF, onOpenOrderForm }) {
  const L = (o) => (lang === "en" ? o.en : o.tr);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [noteType, setNoteType] = useState("call");
  const [noteText, setNoteText] = useState("");
  const [noteFollow, setNoteFollow] = useState("");
  const [busy, setBusy] = useState(false);
  const [itemBusy, setItemBusy] = useState("");
  // Sesli Güncelle state
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const [voiceExtracted, setVoiceExtracted] = useState(null);
  const [voiceForm, setVoiceForm] = useState(null);
  const [voiceSaving, setVoiceSaving] = useState(false);
  const voiceRecRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const voiceTimerRef = useRef(null);

  const stopVoiceTimer = () => { if (voiceTimerRef.current) { clearInterval(voiceTimerRef.current); voiceTimerRef.current = null; } };
  const voiceFmt = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      voiceChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) voiceChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(voiceChunksRef.current, { type: mime || "audio/webm" });
        setVoiceUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          fd.append("durationSec", String(voiceSeconds));
          fd.append("dry_run", "true");
          const { data: res } = await api.post("/crm/leads/from-voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setVoiceExtracted(res);
          setVoiceForm({ ...res.extracted });
        } catch (e) {
          toast.error("Ses işlenemedi — " + (e?.response?.data?.detail || e.message));
        } finally { setVoiceUploading(false); }
      };
      voiceRecRef.current = rec;
      rec.start();
      setVoiceSeconds(0);
      setVoiceRecording(true);
      voiceTimerRef.current = setInterval(() => setVoiceSeconds(s => s + 1), 1000);
    } catch { toast.error("Mikrofona erişilemedi"); }
  };
  const stopVoice = () => { stopVoiceTimer(); setVoiceRecording(false); try { voiceRecRef.current?.stop(); } catch {} };
  const saveVoiceUpdate = async () => {
    if (!voiceForm) return;
    setVoiceSaving(true);
    try {
      const patch = {};
      if (voiceForm.customerName) patch.customerName = voiceForm.customerName;
      if (voiceForm.phone) patch.phone = voiceForm.phone;
      if (voiceForm.email) patch.email = voiceForm.email;
      if (voiceForm.city) patch.city = voiceForm.city;
      if (voiceForm.projectType) patch.projectType = voiceForm.projectType;
      if (voiceForm.notes) patch.notes = voiceForm.notes;
      if (voiceForm.nextFollowUp) patch.nextFollowUp = voiceForm.nextFollowUp;
      if (voiceForm.estimatedValue > 0) patch.estimatedValue = voiceForm.estimatedValue;
      await api.put(`/crm/leads/${leadId}`, patch);
      toast.success("Aday bilgileri sesli kaydile güncellendi");
      setVoiceExtracted(null); setVoiceForm(null);
      await load(); onChanged();
    } catch (e) { toast.error("Güncelleme hatası: " + (e?.response?.data?.detail || e.message)); }
    finally { setVoiceSaving(false); }
  };

  const load = async () => {
    const r = await api.get(`/crm/leads/${leadId}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const addNote = async () => {
    if (!noteText.trim()) { toast.error(tr("Not metni boş")); return; }
    setBusy(true);
    try {
      await api.post("/crm/notes", { leadId, customerId: data?.lead?.customerId || "", type: noteType, text: noteText, nextFollowUp: noteFollow });
      setNoteText(""); setNoteFollow("");
      toast.success(tr("Not eklendi"));
      await load(); onChanged();
    } finally { setBusy(false); }
  };

  const toQuoteItem = async (noteId) => {
    setItemBusy(noteId);
    try {
      const r = await api.post(`/crm/notes/${noteId}/to-quote-item`);
      toast.success(`${tr("Teklif kalemi oluşturuldu")}: #${r.data.quoteNo || r.data.id}`);
      await load(); onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Teklif kalemi oluşturulamadı"));
    } finally { setItemBusy(""); }
  };

  const convert = () => {
    if (onOpenOrderForm) {
      onOpenOrderForm(lead);
      onClose();
    }
  };

  const sendWhatsapp = async () => {
    const raw = String(lead.phone || "").replace(/[^0-9]/g, "");
    if (!raw) { toast.error(tr("Bu kayıtta telefon numarası yok")); return; }
    const intl = raw.startsWith("90") ? raw : raw.replace(/^0+/, "90");
    const who = lead.contactName || lead.customerName || "";
    const msg = lang === "en"
      ? `Hello ${who}, this is ZEN Curtain Systems. Regarding your enquiry "${lead.title || ""}" — may we get in touch?`
      : `Merhaba ${who}, ZEN Mekanizmalı Perde Sistemleri. "${lead.title || ""}" talebinizle ilgili görüşmek istiyoruz.`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
    try {
      await api.post("/crm/notes", {
        leadId, customerId: lead.customerId || "", type: "whatsapp",
        text: `${tr("WhatsApp mesajı gönderildi")} (${intl}): ${msg}`,
      });
      toast.success(tr("WhatsApp açıldı ve nota işlendi"));
      await load(); onChanged();
    } catch { /* not kaydı başarısız olsa da mesaj açıldı */ }
  };

  const removeLead = async () => {
    if (!window.confirm(tr("Bu aday kaydı silinsin mi?"))) return;
    await api.delete(`/crm/leads/${leadId}`);
    toast.success(tr("Silindi"));
    onChanged(); onClose();
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center" onClick={onClose}>
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }

  const lead = data.lead;
  const stage = STAGES.find(s => s.id === lead.stage) || STAGES[0];

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="zen-panel w-full max-w-2xl h-full overflow-y-auto p-5 !rounded-none" onClick={e => e.stopPropagation()} data-testid="crm-lead-detail">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.color }} />
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{L(stage)}</span>
            </div>
            <h3 className="text-xl font-extrabold mt-1">{lead.title || lead.customerName}</h3>
            <div className="text-[12px] text-slate-500 dark:text-slate-400">{lead.customerName} {lead.phone ? `· ${lead.phone}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="crm-lead-edit" onClick={() => setEdit(true)} className="zen-btn-ghost !py-1.5 !px-3 !text-xs">{tr("Düzenle")}</button>
            <button
              data-testid="crm-lead-voice-update-btn"
              onClick={voiceRecording ? stopVoice : startVoice}
              disabled={voiceUploading}
              title={voiceRecording ? "Kaydı durdur ve güncelle" : "Sesle güncelle"}
              className={`zen-btn-ghost !py-1.5 !px-3 !text-xs flex items-center gap-1.5 ${voiceRecording ? "!border-violet-500 !text-violet-500 animate-pulse" : ""} ${voiceUploading ? "opacity-60 cursor-wait" : ""}`}
            >
              {voiceUploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> AI…</> : voiceRecording ? <><Square className="w-3.5 h-3.5" /> {voiceFmt(voiceSeconds)}</> : <><Mic className="w-3.5 h-3.5" /> Sesli</>}
            </button>
            <button data-testid="crm-lead-delete" onClick={removeLead} className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></button>
            <button onClick={onClose} data-testid="crm-lead-detail-close" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-white/10"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {/* Sesli Güncelleme Onay Modali */}
        {voiceExtracted && voiceForm && (
          <div className="mb-4 zen-panel p-4 border-l-4 border-violet-500 zen-anim-up space-y-3" data-testid="voice-update-panel">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-500" />
              <span className="text-xs font-bold text-violet-600 dark:text-violet-400">Sesli Kayıt ile Güncelleme Onayı</span>
              <button onClick={() => { setVoiceExtracted(null); setVoiceForm(null); }} className="ml-auto p-1 rounded hover:bg-slate-100 dark:hover:bg-white/10"><X className="w-3.5 h-3.5" /></button>
            </div>
            {voiceExtracted.transcript && (
              <div className="text-[10px] italic text-slate-500 bg-slate-100 dark:bg-[#0C1220] rounded p-2 max-h-16 overflow-y-auto">{voiceExtracted.transcript}</div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {[["customerName","Müşteri"],["phone","Telefon"],["email","E-posta"],["city","Şehir"],["projectType","Proje Tipi"]].map(([k,l]) => (
                <div key={k}>
                  <label className="text-[10px] text-slate-400 block">{l}</label>
                  <input className="zen-input w-full !text-xs !py-1" value={voiceForm[k] || ""} onChange={e => setVoiceForm(f => ({...f,[k]:e.target.value}))} />
                </div>
              ))}
              <div>
                <label className="text-[10px] text-slate-400 block">Takip Tarihi</label>
                <input type="date" className="zen-input w-full !text-xs !py-1" value={voiceForm.nextFollowUp || ""} onChange={e => setVoiceForm(f => ({...f,nextFollowUp:e.target.value}))} />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-400 block mb-0.5">Notlar</label>
              <textarea className="zen-input w-full !text-xs resize-none" rows={2} value={voiceForm.notes || ""} onChange={e => setVoiceForm(f => ({...f,notes:e.target.value}))} />
            </div>
            <div className="flex gap-2">
              <button onClick={saveVoiceUpdate} disabled={voiceSaving} data-testid="voice-update-save" className="zen-btn-primary !text-xs flex-1 disabled:opacity-50">
                {voiceSaving ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Güncelleniyor…</> : <><Check className="w-3.5 h-3.5 inline mr-1" />Güncelle</>}
              </button>
              <button onClick={() => { setVoiceExtracted(null); setVoiceForm(null); }} className="zen-btn-ghost !text-xs">İptal</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
          <Stat label={tr("Tahmini Tutar")} value={`€${Number(lead.estimatedValue || 0).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")}`} />
          <Stat label={tr("Kaynak")} value={L(SOURCES.find(s => s.id === lead.source) || SOURCES[0])} />
          <Stat label={tr("Sonraki Takip")} value={lead.nextFollowUp || "—"} />
          <Stat label={tr("Sorumlu")} value={lead.ownerName || "—"} />
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <select data-testid="crm-detail-stage" className="zen-input !py-2 !text-xs" value={lead.stage}
            onChange={async (e) => { await api.put(`/crm/leads/${leadId}`, { stage: e.target.value }); await load(); onChanged(); }}>
            {STAGES.map(s => <option key={s.id} value={s.id}>{L(s)}</option>)}
          </select>
          {lead.orderId ? (
            <span className="zen-chip !text-[11px] flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> {tr("Sipariş")}: {lead.orderId}</span>
          ) : (
            <button data-testid="crm-convert-order" disabled={busy} onClick={convert} className="zen-btn-primary !py-2 !px-3.5 !text-xs">
              <ShoppingCart className="w-4 h-4" /> {tr("Tek Tıkla Siparişe Dönüştür")}
            </button>
          )}
          <button data-testid="crm-whatsapp" onClick={sendWhatsapp} className="zen-btn-ghost !py-2 !px-3.5 !text-xs !border-emerald-500/50 !text-emerald-600 dark:!text-emerald-400">
            <Send className="w-4 h-4" /> {tr("WhatsApp Gönder")}
          </button>
          {lead.quoteId && <QuotePdfButton quoteId={lead.quoteId} tr={tr} onGenerateQuotePDF={onGenerateQuotePDF} />}
        </div>

        <VoiceNote leadId={leadId} customerId={lead.customerId} tr={tr} onSaved={async () => { await load(); onChanged(); }} />

        {/* Sesli Not Geçmişi — type=voice notlar ayrı ve öne çıkarılmış */}
        {(() => {
          const voiceNotes = (data.notes || []).filter(n => n.type === "voice");
          if (!voiceNotes.length) return null;
          return (
            <div className="zen-panel p-3.5 mb-4 space-y-3" data-testid="crm-voice-history">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-bold uppercase tracking-wider text-violet-500 flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5" /> {tr("Sesli Not Geçmişi")} ({voiceNotes.length})
                </div>
              </div>
              <div className="space-y-2.5">
                {voiceNotes.map(n => <VoiceNoteHistoryCard key={n.id} n={n} tr={tr} />)}
              </div>
            </div>
          );
        })()}

        <div className="zen-panel p-3.5 mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5">{tr("Görüşme Notu Ekle")}</div>
          <div className="flex flex-wrap gap-2 mb-2">
            {NOTE_TYPES.map(nt => (
              <button key={nt.id} data-testid={`crm-note-type-${nt.id}`} onClick={() => setNoteType(nt.id)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border flex items-center gap-1.5 transition-colors ${noteType === nt.id
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
                  : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300"}`}>
                <nt.icon className="w-3.5 h-3.5" /> {L(nt)}
              </button>
            ))}
          </div>
          <textarea data-testid="crm-note-text" rows={3} className="zen-input w-full" value={noteText}
            onChange={e => setNoteText(e.target.value)} placeholder={tr("Görüşmede konuşulanlar...")} />
          <div className="flex flex-wrap items-end gap-2 mt-2">
            <Field label={tr("Sonraki Takip")}>
              <input data-testid="crm-note-followup" type="date" className="zen-input !py-1.5 !text-xs" value={noteFollow} onChange={e => setNoteFollow(e.target.value)} />
            </Field>
            <button data-testid="crm-note-save" disabled={busy} onClick={addNote} className="zen-btn-primary !py-2 !px-3.5 !text-xs">
              <Plus className="w-4 h-4" /> {tr("Not Ekle")}
            </button>
          </div>
        </div>

        <div className="mb-5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5">{tr("Görüşme Geçmişi")} ({data.notes.length})</div>
          {data.notes.length === 0 ? (
            <div className="text-[12px] text-slate-400">{tr("Henüz not yok")}</div>
          ) : (
            <div className="space-y-2.5" data-testid="crm-note-timeline">
              {data.notes.map(n => {
                const nt = NOTE_TYPES.find(x => x.id === n.type);
                const Icon = n.type === "voice" ? Mic : (nt?.icon || StickyNote);
                const isVoice = n.type === "voice";
                return (
                  <NoteCard key={n.id} n={n} nt={nt} Icon={Icon} isVoice={isVoice}
                    lang={lang} tr={tr} L={L}
                    itemBusy={itemBusy}
                    onToQuoteItem={toQuoteItem}
                    onDelete={async () => { if (!window.confirm("Bu not silinsin mi?")) return; await api.delete(`/crm/notes/${n.id}`); await load(); onChanged(); }}
                    onAttached={async () => { await load(); }}
                  />
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2.5 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {tr("Müşteri Geçmişi")}
          </div>
          {["orders", "quotes", "invoices"].map(k => (
            <div key={k} className="mb-3" data-testid={`crm-history-${k}`}>
              <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">
                {k === "orders" ? tr("Siparişler") : k === "quotes" ? tr("Teklifler") : tr("Faturalar")} ({data.history[k].length})
              </div>
              {data.history[k].length === 0 ? (
                <div className="text-[11px] text-slate-400">—</div>
              ) : (
                <div className="space-y-1">
                  {data.history[k].slice(0, 6).map(x => (
                    <div key={x.id} className="flex items-center justify-between text-[11px] px-2.5 py-1.5 rounded-lg bg-white/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-[#22304A]">
                      <span className="zen-mono">{x.orderNo || x.quoteNo || x.invoiceNo || x.id}</span>
                      <span className="text-slate-500">{(x.createdAt || "").slice(0, 10)}</span>
                      <span className="zen-mono font-semibold">{Number(x.total || 0).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")} {x.currency || ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {edit && (
          <LeadForm lang={lang} tr={tr} customers={customers} quotes={quotes} initial={lead}
            onClose={() => setEdit(false)} onSaved={async () => { setEdit(false); await load(); onChanged(); }} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl p-2.5 border border-slate-200/70 dark:border-[#22304A] bg-white/60 dark:bg-white/[0.03]">
      <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-[13px] font-bold text-slate-800 dark:text-slate-100 mt-0.5">{value}</div>
    </div>
  );
}

/** Akıllı Sesli Not: mikrofonla kayıt → Whisper transkript → AI özet. */
/* ---- Teklif PDF butonu ---- */
function QuotePdfButton({ quoteId, tr, onGenerateQuotePDF }) {
  const [loading, setLoading] = useState(false);

  const openPdf = async () => {
    setLoading(true);
    try {
      // Önce mevcut paylaşım linki var mı bak (WhatsApp ile daha önce paylaşıldıysa tam şablon)
      const { data } = await api.get(`/quotes/${quoteId}/pdf-link`);
      let url;
      if (data.hasFullPdf && data.path) {
        // Daha önce tam şablonla oluşturulmuş PDF var
        url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      } else if (onGenerateQuotePDF) {
        // App.js'deki buildQuoteHTML ile tam şablonu oluştur
        url = await onGenerateQuotePDF(quoteId);
      } else {
        url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      }
      if (url) window.open(url, "_blank");
    } catch {
      toast.error("PDF açılamadı");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={openPdf}
      disabled={loading}
      data-testid="crm-quote-pdf-btn"
      className="zen-btn-ghost !py-2 !px-3.5 !text-xs flex items-center gap-1.5 !border-[#E5322A]/40 !text-[#E5322A]"
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
      Teklif PDF
    </button>
  );
}

/* ── Olay Kartı ── */
function NoteCard({ n, nt, Icon, isVoice, lang, tr, L, itemBusy, onToQuoteItem, onDelete, onAttached }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [attUploading, setAttUploading]     = useState(false);
  const fileRef = useRef(null);

  const uploadAtt = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setAttUploading(true);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.warning(`${f.name}: 5 MB sınırını aşıyor`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f, f.name);
        await api.post(`/crm/notes/${n.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { toast.error(`${tr("Yüklenemedi")}: ${f.name}`); }
    }
    setAttUploading(false);
    toast.success(tr("Görsel eklendi"));
    onAttached?.();
    e.target.value = "";
  };

  const deleteAtt = async (attId) => {
    if (!window.confirm("Bu ek (dosya) silinsin mi?")) return;
    await api.delete(`/crm/attachments/${attId}`);
    onAttached?.();
  };

  const atts = n.attachments || [];

  return (
    <div className={`rounded-xl border ${isVoice ? "border-indigo-500/25 bg-indigo-500/[0.03]" : "border-slate-200/70 dark:border-[#22304A] bg-white/60 dark:bg-white/[0.03]"}`}
      data-testid={`crm-note-${n.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
        <span className={`flex items-center gap-1.5 text-[11px] font-bold ${isVoice ? "text-indigo-500" : "text-slate-600 dark:text-slate-300"}`}>
          <Icon className="w-3.5 h-3.5" />
          {isVoice ? tr("Sesli Olay Kartı") : (nt ? L(nt) : n.type)}
          {isVoice && n.durationSec > 0 && (
            <span className="text-[10px] font-normal text-slate-400">
              · {Math.floor(n.durationSec / 60)}:{String(Math.floor(n.durationSec % 60)).padStart(2, "0")} dk
            </span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 zen-mono hidden sm:block">
            {new Date(n.createdAt).toLocaleString(lang === "en" ? "en-GB" : "tr-TR")} · {n.createdBy}
          </span>
          <button onClick={() => fileRef.current?.click()} disabled={attUploading}
            data-testid={`crm-note-attach-${n.id}`}
            className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-white/10 text-slate-400 hover:text-indigo-500 transition-colors"
            title={tr("Görsel Ekle")}>
            {attUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
          </button>
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={uploadAtt} />
          <button onClick={onDelete} className="p-1 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-300 hover:text-rose-500 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="px-3 pb-3 space-y-2">
        {/* AI Özeti */}
        {n.summary && (
          <div className="rounded-lg p-2.5 bg-indigo-500/8 border border-indigo-500/20">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-indigo-500 mb-1">
              <Sparkles className="w-3 h-3" /> {tr("AI Özeti")}
            </div>
            <div className="text-[12px] text-slate-700 dark:text-slate-200 leading-relaxed">{n.summary}</div>
            {(n.highlights || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {n.highlights.map((h, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-md bg-white/70 dark:bg-white/10 border border-indigo-500/20">
                    <b>{h.label}:</b> {h.value}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manuel not metni (sesli notlarda transkript olarak göster) */}
        {n.text && !isVoice && (
          <div className="text-[12px] text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{n.text}</div>
        )}

        {/* Transkript (sesli not) — açılır/kapanır */}
        {isVoice && (n.transcript || n.text) && (
          <div className="rounded-lg border border-slate-200 dark:border-[#22304A] overflow-hidden">
            <button onClick={() => setShowTranscript(v => !v)}
              className="w-full flex items-center justify-between px-2.5 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
              <span className="flex items-center gap-1.5"><FileText className="w-3 h-3" /> {tr("Transkript (Ham Kayıt)")}</span>
              {showTranscript ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showTranscript && (
              <div className="px-2.5 pb-2.5 text-[11px] text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed border-t border-slate-100 dark:border-[#22304A] pt-2">
                {n.transcript || n.text}
              </div>
            )}
          </div>
        )}

        {/* Görsel ekler */}
        {atts.length > 0 && (
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {tr("Ekler")} ({atts.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {atts.map(att => (
                att.contentType?.startsWith("image/") ? (
                  <AttachmentThumb key={att.id} attId={att.id} filename={att.filename}
                    onDelete={() => deleteAtt(att.id)} />
                ) : (
                  <a key={att.id} href={`/api/crm/attachments/${att.id}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#22304A] text-[11px] hover:border-indigo-400 transition-colors">
                    <FileText className="w-3.5 h-3.5 text-slate-400" /> {att.filename}
                  </a>
                )
              ))}
            </div>
          </div>
        )}

        {/* Önerilen teklif kalemi */}
        {n.suggestedItem && (
          <div className="rounded-lg p-2.5 bg-emerald-500/8 border border-emerald-500/25" data-testid={`crm-note-item-${n.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-1">
              <ShoppingCart className="w-3 h-3" /> {tr("Önerilen Teklif Kalemi")}
            </div>
            <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">
              {n.suggestedItem.product}{n.suggestedItem.fabric ? ` · ${n.suggestedItem.fabric}` : ""}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 zen-mono">
              {n.suggestedItem.widthCm}×{n.suggestedItem.heightCm} cm · {n.suggestedItem.qty} {tr("adet")}
              {n.suggestedItem.unitPrice ? ` · ${n.suggestedItem.currency} ${n.suggestedItem.unitPrice}` : ""}
            </div>
            {n.quoteId ? (
              <span className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <Check className="w-3.5 h-3.5" /> {tr("Teklife eklendi")}: {n.quoteId}
              </span>
            ) : (
              <button data-testid={`crm-note-to-quote-${n.id}`} disabled={itemBusy === n.id}
                onClick={() => onToQuoteItem(n.id)}
                className="zen-btn-primary !py-1.5 !px-3 !text-[11px] mt-2">
                {itemBusy === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                {tr("Teklif Kalemine Dönüştür")}
              </button>
            )}
          </div>
        )}

        {/* Takip tarihi */}
        {n.nextFollowUp && (
          <div className="text-[10px] text-sky-500 flex items-center gap-1">
            <CalendarClock className="w-3 h-3" /> {n.nextFollowUp}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Ek dosya/görsel thumbnail ── */
function AttachmentThumb({ attId, filename, onDelete }) {
  const [src, setSrc] = useState(null);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let objUrl = null;
    api.get(`/crm/attachments/${attId}`, { responseType: "blob" })
      .then(r => { objUrl = URL.createObjectURL(r.data); setSrc(objUrl); })
      .catch(() => setSrc("error"));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [attId]);

  if (!src) return <div className="w-16 h-16 rounded-lg bg-slate-200 dark:bg-[#1A2340] animate-pulse shrink-0" />;
  if (src === "error") return (
    <div className="w-16 h-16 rounded-lg bg-rose-100 dark:bg-rose-900/20 flex items-center justify-center shrink-0">
      <ImageIcon className="w-5 h-5 text-rose-400" />
    </div>
  );

  const isImage = true;
  return (
    <>
      <div className="relative group shrink-0 w-16 h-16">
        <img src={src} alt={filename} onClick={() => setLightbox(true)}
          className="w-16 h-16 object-cover rounded-lg cursor-zoom-in border border-slate-200 dark:border-[#22304A]" />
        <div className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
          <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        {onDelete && (
          <button onClick={onDelete}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 rounded-full text-white hidden group-hover:flex items-center justify-center shadow-sm">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
      {lightbox && (
        <div className="fixed inset-0 z-[200] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}>
          <img src={src} alt={filename} className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl" />
          <button className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
        </div>
      )}
    </>
  );
}

/** Sesli Not Geçmişi — transkript + özet kart */
function VoiceNoteHistoryCard({ n, tr }) {
  const [open, setOpen] = useState(false);
  const fmtDur = (s) => s > 0 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} dk` : "";
  const dateStr = n.createdAt ? new Date(n.createdAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="rounded-xl border border-violet-400/20 bg-violet-500/5 overflow-hidden" data-testid={`voice-history-card-${n.id}`}>
      <div className="px-3 py-2.5 flex items-start gap-2.5">
        <div className="w-7 h-7 rounded-full bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Mic className="w-3.5 h-3.5 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold text-violet-600 dark:text-violet-400">{dateStr}</span>
            {n.durationSec > 0 && <span className="text-[10px] text-slate-400 bg-slate-100 dark:bg-[#0C1220] px-1.5 py-0.5 rounded">{fmtDur(n.durationSec)}</span>}
            {n.createdBy && <span className="text-[10px] text-slate-400">{n.createdBy}</span>}
          </div>
          {/* AI Özeti */}
          {n.summary && (
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">{n.summary}</p>
          )}
          {/* Takip tarihi */}
          {n.nextFollowUp && (
            <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
              <CalendarClock className="w-3 h-3" /> {n.nextFollowUp}
            </div>
          )}
        </div>
      </div>
      {/* Transkript aç/kapat */}
      {(n.transcript || n.text) && (
        <div className="border-t border-violet-400/15">
          <button
            onClick={() => setOpen(v => !v)}
            data-testid={`voice-history-transcript-toggle-${n.id}`}
            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-violet-500/5 transition-colors"
          >
            <span className="flex items-center gap-1.5"><FileText className="w-3 h-3" /> {tr("Transkript")}</span>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {open && (
            <div className="px-3 pb-3 text-[11px] text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-50 dark:bg-[#0C1220] border-t border-violet-400/10 pt-2">
              {n.transcript || n.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VoiceNote({ leadId, customerId, tr, onSaved }) {
  const [recording, setRecording]     = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [seconds, setSeconds]         = useState(0);
  const [pendingFiles, setPendingFiles] = useState([]);   // {file, preview}[]
  const recRef   = useRef(null);
  const chunksRef = useRef([]);
  const timerRef  = useRef(null);
  const fileRef   = useRef(null);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        await upload(blob, seconds);
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setRecording(true);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch { toast.error(tr("Mikrofona erişilemedi")); }
  };

  const stop = () => { stopTimer(); setRecording(false); try { recRef.current?.stop(); } catch {} };

  const pickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const added = files
      .filter(f => f.size <= 5 * 1024 * 1024)
      .map(f => ({ file: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
    if (files.length > added.length) toast.warning(tr("5 MB'ı aşan dosyalar atlandı"));
    setPendingFiles(p => [...p, ...added]);
    e.target.value = "";
  };

  const removeFile = (idx) => {
    setPendingFiles(p => {
      if (p[idx]?.preview) URL.revokeObjectURL(p[idx].preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  const uploadAttachments = async (noteId) => {
    for (const pf of pendingFiles) {
      try {
        const fd = new FormData();
        fd.append("file", pf.file, pf.file.name);
        await api.post(`/crm/notes/${noteId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch { toast.error(`${tr("Yüklenemedi")}: ${pf.file.name}`); }
    }
    setPendingFiles([]);
  };

  const upload = async (blob, dur) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      fd.append("leadId", leadId || "");
      fd.append("customerId", customerId || "");
      fd.append("durationSec", String(dur || 0));
      const res = await api.post("/crm/notes/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (pendingFiles.length > 0 && res.data?.id) {
        toast.info(tr("Görseller yükleniyor..."));
        await uploadAttachments(res.data.id);
      }
      toast.success(tr("Sesli not yazıya çevrildi ve kaydedildi"));
      onSaved();
    } catch { toast.error(tr("Sesli not kaydedilemedi")); }
    finally { setUploading(false); }
  };

  /* Sadece görsel yüklenecek, ses yok */
  const saveOnlyFiles = async () => {
    if (!pendingFiles.length) return;
    setUploading(true);
    try {
      const noteRes = await api.post("/crm/notes", {
        leadId, customerId, type: "note",
        text: tr("Görsel ek(ler) eklendi"),
      });
      await uploadAttachments(noteRes.data.id);
      toast.success(tr("Görseller kaydedildi"));
      onSaved();
    } catch { toast.error(tr("Görseller kaydedilemedi")); }
    finally { setUploading(false); }
  };

  return (
    <div className="zen-panel p-3.5 mb-4 space-y-3" data-testid="crm-voice-note">
      {/* Ses kaydı bölümü */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-500 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {tr("Akıllı Sesli Not")}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            {tr("Kaydı bitirdiğinizde konuşma otomatik yazıya çevrilir ve önemli detaylar özetlenir.")}
          </div>
        </div>
        {uploading ? (
          <span className="flex items-center gap-2 text-[11px] font-semibold text-indigo-500 shrink-0">
            <Loader2 className="w-4 h-4 animate-spin" /> {tr("Kaydediliyor...")}
          </span>
        ) : recording ? (
          <button data-testid="crm-voice-stop" onClick={stop} className="zen-btn-primary !py-2 !px-3.5 !text-xs !bg-rose-600 shrink-0">
            <Square className="w-3.5 h-3.5" /> {tr("Kaydı Bitir")} · {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
          </button>
        ) : (
          <button data-testid="crm-voice-start" onClick={start} className="zen-btn-ghost !py-2 !px-3.5 !text-xs shrink-0">
            <Mic className="w-4 h-4" /> {tr("Sesli Not Al")}
          </button>
        )}
      </div>

      {/* Görsel / Dosya ekleme */}
      <div className="border-t border-slate-100 dark:border-[#22304A] pt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Paperclip className="w-3.5 h-3.5" /> {tr("Dosya / Görsel Ekle")}
          </span>
          <button
            data-testid="crm-attach-btn"
            onClick={() => fileRef.current?.click()}
            className="zen-btn-ghost !py-1 !px-2.5 !text-[11px]">
            <Plus className="w-3 h-3" /> {tr("Seç")}
          </button>
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf" className="hidden" onChange={pickFiles} />
          {pendingFiles.length > 0 && !recording && (
            <button onClick={saveOnlyFiles} disabled={uploading}
              className="zen-btn-primary !py-1 !px-2.5 !text-[11px] ml-auto">
              <Check className="w-3 h-3" /> {tr("Sadece Görselleri Kaydet")} ({pendingFiles.length})
            </button>
          )}
        </div>
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {pendingFiles.map((pf, i) => (
              <div key={i} className="relative group w-14 h-14 shrink-0">
                {pf.preview ? (
                  <img src={pf.preview} alt={pf.file.name}
                    className="w-14 h-14 object-cover rounded-lg border border-slate-200 dark:border-[#22304A]" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-slate-100 dark:bg-[#1A2340] flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-400" />
                  </div>
                )}
                <button onClick={() => removeFile(i)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 rounded-full text-white hidden group-hover:flex items-center justify-center">
                  <X className="w-2.5 h-2.5" />
                </button>
                <div className="text-[9px] text-slate-400 truncate w-14 mt-0.5">{pf.file.name}</div>
              </div>
            ))}
          </div>
        )}
        {pendingFiles.length === 0 && (
          <div className="text-[10px] text-slate-400 dark:text-slate-600">{tr("El yazısı ölçü kağıtları, fotoğraflar veya PDF'ler ekleyebilirsiniz (maks. 5 MB)")}</div>
        )}
      </div>
    </div>
  );
}
